/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, ReactNode} from 'react';
import IconButton from '@material-ui/core/IconButton';
import {makeStyles} from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {SideNavCollapseBarIcon} from '../../../icons/current/SideNavCollapseBarIcon';
import {SideNavExpandBarIcon} from '../../../icons/current/SideNavExpandBarIcon';
import Render from '../../../render/Render';
import {dlsColor, dlsShadow, dlsZIndex} from '../../../theme/theme';

/**
 * Defines the props for the current-navigation header region.
 */
interface NavigationHeaderProps {
  collapsed: boolean;
  content?: ReactNode;
  mobile?: boolean;
  onHandleCollapse?: Function;
  useExpandCollapseFeature?: boolean;
}

/**
 * Defines the style props used by the current-navigation header styles.
 */
interface StyleProps {
  collapsed?: boolean;
  mobile?: boolean;
}

// Figma: the nav's own 8px inset - all round when expanded, top only when
// collapsed (its 48px width centres everything); the phone sidesheet keeps
// its earlier inset ...
const getSectionPadding = ({collapsed, mobile}: StyleProps): string => {
  let padding = mobile ? '8px 12px 12px' : '8px 8px 0';
  if (collapsed) {
    padding = '8px 0 0';
  }
  return padding;
};

const useStyles = makeStyles(() => ({
  section : {
    display       : 'flex',
    flexDirection : 'column',
    flexShrink    : 0,
    fontFamily    : 'Inter',
    overflow      : 'hidden',
    padding       : getSectionPadding,
    position      : 'relative'
  },
  collapseButton : {
    alignSelf    : 'flex-end',
    borderRadius : 8,
    marginRight  : 8,
    marginTop    : 4,
    padding      : 4,
    '&:hover'    : {
      backgroundColor : dlsColor('grey8')
    }
  },
  collapseButtonExpanded : {
    marginRight : 0,
    marginTop   : 0,
    position    : 'absolute',
    right       : 16,
    top         : 14
  },
  collapseButtonCollapsed : {
    left        : '50%',
    marginLeft  : 0,
    marginRight : 0,
    position    : 'absolute',
    top         : 52,
    transform   : 'translateX(-50%)',
    zIndex      : 1
  },
  // the expand / collapse label shares the flyout's layer, so it stays above
  // any page scrim drawn over the content beside the menu ...
  tooltipPopper : {
    zIndex : `${dlsZIndex('navigationFlyout')} !important`
  },
  tooltip : {
    backgroundColor : 'transparent',
    marginLeft      : '8px !important',
    maxWidth        : 'none',
    padding         : '0 !important'
  },
  tooltipContent : {
    alignItems      : 'center',
    backgroundColor : dlsColor('white'),
    border          : `1px solid ${dlsColor('grey5')}`,
    borderRadius    : 8,
    boxShadow       : dlsShadow(3.2),
    boxSizing       : 'border-box',
    color           : dlsColor('lightTeal'),
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 14,
    fontWeight      : 500,
    gap             : 8,
    height          : 32,
    lineHeight      : '17px',
    minHeight       : 32,
    padding         : '4px 16px',
    whiteSpace      : 'nowrap'
  }
}));

/**
 * Renders the current-theme header region and collapse/expand control.
 *
 * @param props Defines the header content and collapse behavior.
 * @returns The current navigation header section.
 */
const NavigationHeader = ({
  collapsed,
  content,
  mobile = false,
  onHandleCollapse,
  useExpandCollapseFeature = false
}: NavigationHeaderProps) => {
  const classes = useStyles({collapsed, mobile});

  if (!content) {
    return null;
  }

  return (
    <div className={classes.section}>
      {content}
      {useExpandCollapseFeature && (
        <Tooltip title={(
          <div className={classes.tooltipContent}>
            <span>{translate(`nav:menu.${collapsed ? 'expand' : 'collapse'}`)}</span>
          </div>
        )}
        placement='right'
        enterDelay={0}
        leaveDelay={0}
        classes={{
          popper  : classes.tooltipPopper,
          tooltip : classes.tooltip
        }}>
          <IconButton className={classnames(classes.collapseButton, {
            [classes.collapseButtonExpanded]  : !collapsed,
            [classes.collapseButtonCollapsed] : collapsed
          })}
          aria-label={translate(`nav:menu.${collapsed ? 'expand' : 'collapse'}`)}
          onClick={onHandleCollapse as any}>
            <Render>
              <Render.If condition={collapsed}>
                <SideNavExpandBarIcon color={dlsColor('grey1')} />
              </Render.If>
              <Render.Else>
                <SideNavCollapseBarIcon color={dlsColor('grey1')} />
              </Render.Else>
            </Render>
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

export default memo(NavigationHeader);
